<template>
  <div
    class="flex flex-col items-center justify-center w-full pb-20 bg-[#F2F6FA]"
  >
    <section class="flex flex-col items-center w-full justify-center">
      <div
        class="flex items-center justify-between md:mt-5 px-4 md:px-0 w-full"
      >
        <div class="flex items-center gap-2">
          <v-icon
            :style="{
              color: theme !== '' ? theme : '#8056dd',
              fontSize: '35px',
            }"
            >mdi-link-variant</v-icon
          >
          <p class="text-[22px] font-bold">Add Locations to Schedule Posts</p>
        </div>
        <div class="flex items-center gap-2">
          <v-btn
            v-if="hasAnySelectedLocations"
            color="success"
            outlined
            @click="connectAllSelectedLocations"
            :loading="connectingAll"
            :disabled="hasConnectionsInProgress"
            class="page-add-all-btn"
          >
            <v-icon left size="16">mdi-link-variant-plus</v-icon>
            Add All Selected Locations
          </v-btn>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center mt-10"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
        <p class="mt-4 text-gray-600">Adding locations to scheduled posts...</p>
      </div>

      <!-- Results Display -->
      <div v-else class="w-full mx-auto mt-8 px-4">
        <!-- Success Response -->
        <div v-if="response">
          <!-- Records Display -->
          <div v-if="response.records && response.records.length > 0">
            <div
              class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <!-- Table Rows -->
              <div class="table-body">
                <div
                  v-for="(record, index) in response.records"
                  :key="index"
                  class="table-row"
                >
                  <!-- Post Column (Image) -->
                  <div class="table-cell post-cell">
                    <div class="post-number">{{ index + 1 }}</div>
                    <div v-if="record.image" class="post-image-container-small">
                      <img
                        :src="record.image"
                        :alt="record.post_text || 'Post image'"
                        class="post-image-small"
                      />
                    </div>
                    <div
                      v-else
                      class="post-image-container-small bg-gray-100 flex items-center justify-center"
                    >
                      <v-icon color="gray" size="20">mdi-image-off</v-icon>
                    </div>
                  </div>

                  <!-- Content Column -->
                  <div class="table-cell content-cell">
                    <div class="content-section">
                      <div class="scheduled-time">
                        <v-icon size="16" color="#6b7280"
                          >mdi-calendar-clock</v-icon
                        >
                        <span class="time-text">{{
                          formatDate(record.scheduled_at)
                        }}</span>
                      </div>
                      <div class="post-text">
                        {{ record.post_text || "No post text available" }}
                      </div>
                    </div>
                  </div>

                  <!-- Locations Column -->
                  <div class="table-cell locations-cell">
                    <div
                      v-if="
                        record.missing_location_ids &&
                        record.missing_location_ids.length > 0
                      "
                      class="locations-dropdown-container"
                    >
                      <v-menu
                        offset-y
                        :close-on-content-click="false"
                        max-height="400"
                        v-model="record.dropdownOpen"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            outlined
                            small
                            class="locations-dropdown-btn"
                          >
                            <v-icon left size="16"
                              >mdi-map-marker-multiple</v-icon
                            >
                            {{
                              getSelectedCount(record) ===
                              record.missing_location_ids.length
                                ? "All locations selected"
                                : `${getSelectedCount(record)} of ${
                                    record.missing_location_ids.length
                                  } selected`
                            }}
                            <v-icon right size="16">mdi-chevron-down</v-icon>
                          </v-btn>
                        </template>

                        <v-card class="locations-dropdown-card">
                          <!-- Search Bar -->
                          <div class="search-container">
                            <v-text-field
                              v-model="record.locationSearch"
                              placeholder="Search locations..."
                              dense
                              outlined
                              hide-details
                              prepend-inner-icon="mdi-magnify"
                              class="search-field"
                              @input="filterLocations(record)"
                            ></v-text-field>
                          </div>

                          <!-- Select All Option -->
                          <div class="select-all-container">
                            <v-checkbox
                              :indeterminate="isIndeterminate(record)"
                              :input-value="areAllSelected(record)"
                              color="primary"
                              class="select-all-checkbox"
                              hide-details
                              @change="toggleSelectAll(record)"
                            >
                              <template v-slot:label>
                                <span class="select-all-text">Select All</span>
                              </template>
                            </v-checkbox>
                          </div>

                          <!-- Locations List -->
                          <div class="dropdown-locations-list">
                            <div
                              v-for="(
                                location, locationIndex
                              ) in getFilteredLocations(record)"
                              :key="locationIndex"
                              class="dropdown-location-item"
                            >
                              <v-checkbox
                                v-model="location.selected"
                                color="primary"
                                class="location-dropdown-checkbox"
                                hide-details
                                @change="
                                  onLocationSelectionChange(record, location)
                                "
                              >
                                <template v-slot:label>
                                  <div class="location-dropdown-info">
                                    <div class="location-dropdown-name">
                                      {{ location.name }}
                                    </div>
                                    <div
                                      class="location-dropdown-address"
                                      v-if="location.location"
                                    >
                                      {{ location.location }}
                                    </div>
                                  </div>
                                </template>
                              </v-checkbox>
                            </div>
                          </div>
                        </v-card>
                      </v-menu>
                    </div>
                    <div v-else class="no-locations">
                      <v-icon size="16" color="success"
                        >mdi-check-circle</v-icon
                      >
                      <span class="no-locations-text">All Added</span>
                    </div>
                  </div>

                  <!-- Actions Column -->
                  <div class="table-cell actions-cell">
                    <v-btn
                      v-if="
                        record.missing_location_ids &&
                        record.missing_location_ids.length > 0
                      "
                      color="primary"
                      small
                      :disabled="
                        !hasSelectedLocations(record) ||
                        connectingRecords.has(record.document_id)
                      "
                      :loading="connectingRecords.has(record.document_id)"
                      @click="connectSelectedLocations(record)"
                      class="connect-btn"
                    >
                      <v-icon left size="16">mdi-link-variant</v-icon>
                      Add ({{ getSelectedCount(record) }})
                    </v-btn>
                    <div v-else class="action-icons">
                      <v-icon color="success" size="24"
                        >mdi-check-circle</v-icon
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Records State -->
          <div
            v-else-if="response.records && response.records.length === 0"
            class="text-center py-8 bg-white rounded-lg shadow-sm border border-gray-200"
          >
            <v-icon color="orange" size="48" class="mb-4"
              >mdi-calendar-remove</v-icon
            >
            <h3 class="text-lg font-semibold text-gray-800 mb-2">
              No Scheduled Posts
            </h3>
            <p class="text-gray-600">
              No scheduled posts missing locations were found.
            </p>
            <!-- <div class="mt-4">
              <router-link
                to="/create"
                class="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <v-icon left>mdi-plus</v-icon>
                Create New Post
              </router-link>
            </div> -->
          </div>
        </div>

        <!-- Error State -->
        <div
          v-if="error"
          class="bg-red-50 border border-red-200 rounded-lg p-6 shadow-sm"
        >
          <div class="flex items-center gap-3">
            <v-icon color="error" size="24">mdi-alert-circle</v-icon>
            <h3 class="text-lg font-semibold text-red-800">Error</h3>
          </div>
          <p class="text-red-600 mt-2">{{ error }}</p>
          <div class="mt-4">
            <v-btn color="primary" @click="connectSchedulePosts">
              <v-icon left>mdi-refresh</v-icon>
              Try Again
            </v-btn>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "@/utils/firebase.utils";
import commonMixins from "@/mixins/common.mixins";

export default {
  mixins: [commonMixins],
  data() {
    return {
      loading: false,
      response: null,
      error: null,
      theme: "",
      connectingRecords: new Set(), // Track which records are being connected
      connectingAll: false, // Track if "Add All" is in progress
    };
  },

  computed: {
    hasAnyMissingLocations() {
      return (
        this.response &&
        this.response.records &&
        this.response.records.some(
          (record) =>
            record.missing_location_ids &&
            record.missing_location_ids.length > 0
        )
      );
    },

    effectiveUID() {
      // Check for conf query parameter first
      const confParam = this.$route.query.conf;
      if (confParam && confParam.includes("_")) {
        const [uid] = confParam.split("_");
        return uid;
      }
      // Fallback to component's default UID
      return this.currentUID;
    },

    effectiveWID() {
      // Check for conf query parameter first
      const confParam = this.$route.query.conf;
      if (confParam && confParam.includes("_")) {
        const [, wid] = confParam.split("_");
        return wid;
      }
      // Fallback to component's default WID
      return this.WID;
    },

    hasConnectionsInProgress() {
      return this.connectingRecords.size > 0 || this.connectingAll;
    },

    hasAnySelectedLocations() {
      return (
        this.response &&
        this.response.records &&
        this.response.records.some(
          (record) =>
            record.missing_location_ids &&
            record.missing_location_ids.some((location) => location.selected)
        )
      );
    },
  },

  async mounted() {
    await this.connectSchedulePosts();
  },

  methods: {
    async connectSchedulePosts() {
      this.loading = true;
      this.error = null;
      this.response = null;

      try {
        // Check if using query parameter values
        const confParam = this.$route.query.conf;
        if (confParam) {
          console.log("Using conf parameter from URL:", confParam);
        } else {
          console.log("Using default UID/WID from component");
        }

        // Prepare payload
        const payload = {
          uid: this.effectiveUID,
          wid: this.effectiveWID,
        };

        console.log(
          "Calling connectSchedulePostLocations with payload:",
          payload
        );

        // Call the cloud function
        const connectSchedulePostLocations = httpsCallable(
          functions,
          "connectSchedulePostLocations"
        );

        const result = await connectSchedulePostLocations(payload);

        console.log("Cloud function response:", result.data);

        // Store the response
        this.response = result.data;

        // Initialize dropdown properties and set all locations as selected by default
        if (this.response.records) {
          this.response.records.forEach((record) => {
            if (record.missing_location_ids) {
              record.locationSearch = "";
              record.dropdownOpen = false;
              record.filteredLocations = [...record.missing_location_ids];

              // Set all locations as selected by default
              record.missing_location_ids.forEach((location) => {
                this.$set(location, "selected", true);
              });
            }
          });
        }
      } catch (error) {
        console.error("Error connecting schedule posts:", error);
        this.error =
          error.message ||
          "An error occurred while connecting scheduled posts to locations.";
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateInput) {
      if (!dateInput) return "Unknown";

      try {
        // Handle different date formats
        let date;
        if (dateInput.seconds) {
          // Firestore Timestamp
          date = new Date(dateInput.seconds * 1000);
        } else if (
          typeof dateInput === "string" ||
          typeof dateInput === "number"
        ) {
          date = new Date(dateInput);
        } else {
          date = dateInput;
        }

        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
      } catch (error) {
        console.error("Error formatting date:", error);
        return "Invalid Date";
      }
    },

    onLocationSelectionChange(record, location) {
      console.log("Location selection changed:", {
        record: record.document_id,
        location: location.name,
        selected: location.selected,
      });

      // You can add logic here to handle the selection change
      // For example, make an API call to update the connection
      // or store the selection for batch processing
    },

    hasSelectedLocations(record) {
      if (!record.missing_location_ids) return false;
      return record.missing_location_ids.some((location) => location.selected);
    },

    getSelectedCount(record) {
      if (!record.missing_location_ids) return 0;
      return record.missing_location_ids.filter((location) => location.selected)
        .length;
    },

    async connectSelectedLocations(record) {
      const selectedLocations = record.missing_location_ids.filter(
        (location) => location.selected
      );

      if (selectedLocations.length === 0) {
        this.$snackbar?.show(
          "Please select at least one location to connect.",
          "warning"
        );
        return;
      }

      // Add this record to connecting set
      this.connectingRecords.add(record.document_id);

      try {
        console.log("Connecting selected locations:", {
          postId: record.document_id,
          selectedLocations: selectedLocations.map((loc) => ({
            id: loc.id,
            name: loc.name,
          })),
        });

        // Prepare records array for this specific post with only selected locations
        const connectRecords = [
          {
            document_id: record.document_id,
            missing_location_ids: selectedLocations,
          },
        ];

        // Prepare payload for cloud function
        const payload = {
          uid: this.effectiveUID,
          wid: this.effectiveWID,
          records: connectRecords,
        };

        console.log(
          "Calling processConnectPostsRecords with payload:",
          payload
        );

        // Call the cloud function
        const processConnectPostsRecords = httpsCallable(
          functions,
          "processConnectPostsRecords"
        );

        const result = await processConnectPostsRecords(payload);

        console.log("Cloud function response:", result.data);

        if (result.data.success) {
          // Remove successfully connected locations from the missing list
          const connectedLocationIds = [];

          if (
            result.data.successful_connections &&
            result.data.successful_connections.length > 0
          ) {
            const successfulConnection = result.data.successful_connections[0];
            connectedLocationIds.push(
              ...successfulConnection.locations_connected
            );
          }

          // Remove connected locations from missing_location_ids
          record.missing_location_ids = record.missing_location_ids.filter(
            (location) => !connectedLocationIds.includes(location.id)
          );

          this.$snackbar?.show(
            result.data.message ||
              `Successfully connected ${selectedLocations.length} location(s)!`,
            "success"
          );

          // Log detailed results
          if (result.data.results) {
            console.log("Detailed results:", result.data.results);
          }
        } else {
          throw new Error(
            result.data.error || result.data.message || "Unknown error occurred"
          );
        }
      } catch (error) {
        console.error("Error connecting locations:", error);

        let errorMessage = "Failed to connect locations. Please try again.";
        if (error.message) {
          errorMessage = error.message;
        }

        this.$snackbar?.show(errorMessage, "error");
      } finally {
        // Remove this record from connecting set
        this.connectingRecords.delete(record.document_id);
      }
    },

    // Dropdown functionality methods
    filterLocations(record) {
      if (!record.locationSearch) {
        record.filteredLocations = [...record.missing_location_ids];
      } else {
        const searchTerm = record.locationSearch.toLowerCase();
        record.filteredLocations = record.missing_location_ids.filter(
          (location) =>
            location.name.toLowerCase().includes(searchTerm) ||
            (location.storeLocation &&
              location.storeLocation.toLowerCase().includes(searchTerm)) ||
            (location.location &&
              location.location.toLowerCase().includes(searchTerm))
        );
      }
    },

    getFilteredLocations(record) {
      return record.filteredLocations || record.missing_location_ids || [];
    },

    isIndeterminate(record) {
      if (!record.missing_location_ids) return false;
      const selectedCount = record.missing_location_ids.filter(
        (loc) => loc.selected
      ).length;
      return (
        selectedCount > 0 && selectedCount < record.missing_location_ids.length
      );
    },

    areAllSelected(record) {
      if (
        !record.missing_location_ids ||
        record.missing_location_ids.length === 0
      )
        return false;
      return record.missing_location_ids.every((loc) => loc.selected);
    },

    toggleSelectAll(record) {
      if (!record.missing_location_ids) return;

      const allSelected = this.areAllSelected(record);
      record.missing_location_ids.forEach((location) => {
        location.selected = !allSelected;
      });
    },

    async connectAllSelectedLocations() {
      if (!this.response || !this.response.records) {
        return;
      }

      // Set loading state for "Add All" button
      this.connectingAll = true;

      try {
        console.log(
          "Starting to connect all selected locations for all posts..."
        );

        // Prepare records array with only posts that have missing locations and selected locations
        const connectRecords = [];
        let totalLocationsToAdd = 0;

        // Prepare the connect records with only currently selected locations
        for (const record of this.response.records) {
          if (
            record.missing_location_ids &&
            record.missing_location_ids.length > 0
          ) {
            const selectedLocations = record.missing_location_ids.filter(
              (location) => location.selected
            );

            if (selectedLocations.length > 0) {
              connectRecords.push({
                document_id: record.document_id,
                missing_location_ids: selectedLocations,
              });
              totalLocationsToAdd += selectedLocations.length;
            }
          }
        }

        if (connectRecords.length === 0) {
          this.$snackbar?.show(
            "No locations are currently selected to connect.",
            "warning"
          );
          return;
        }

        // Prepare payload for cloud function
        const payload = {
          uid: this.effectiveUID,
          wid: this.effectiveWID,
          records: connectRecords,
        };

        console.log(
          "Calling processConnectPostsRecords with payload:",
          payload
        );

        // Call the cloud function
        const processConnectPostsRecords = httpsCallable(
          functions,
          "processConnectPostsRecords"
        );

        const result = await processConnectPostsRecords(payload);

        console.log("Cloud function response:", result.data);

        if (result.data.success) {
          // Remove successfully connected locations from the missing lists
          if (
            result.data.successful_connections &&
            result.data.successful_connections.length > 0
          ) {
            result.data.successful_connections.forEach((connection) => {
              const record = this.response.records.find(
                (r) => r.document_id === connection.document_id
              );

              if (record && connection.locations_connected) {
                // Remove connected locations from missing_location_ids
                record.missing_location_ids =
                  record.missing_location_ids.filter(
                    (location) =>
                      !connection.locations_connected.includes(location.id)
                  );
              }
            });
          }

          this.$snackbar?.show(
            result.data.message ||
              `Successfully connected ${totalLocationsToAdd} selected location(s) across all posts!`,
            "success"
          );

          // Log detailed results
          if (result.data.results) {
            console.log("Detailed results:", result.data.results);
          }

          // Log summary
          if (result.data.summary) {
            console.log("Summary:", result.data.summary);
          }

          // Log any errors
          if (result.data.errors && result.data.errors.length > 0) {
            console.warn("Some errors occurred:", result.data.errors);

            // Show additional warning if there were partial failures
            if (
              result.data.errors.length > 0 &&
              result.data.successful_connections.length > 0
            ) {
              this.$snackbar?.show(
                `Some connections failed. Check console for details.`,
                "warning"
              );
            }
          }
        } else {
          throw new Error(
            result.data.error || result.data.message || "Unknown error occurred"
          );
        }
      } catch (error) {
        console.error("Error connecting all locations:", error);

        let errorMessage =
          "Failed to connect selected locations. Please try again.";
        if (error.message) {
          errorMessage = error.message;
        }

        this.$snackbar?.show(errorMessage, "error");
      } finally {
        // Reset loading state for "Add All" button
        this.connectingAll = false;
      }
    },
  },
};
</script>

<style scoped>
/* Table Layout Styles */
.table-header {
  display: flex;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 16px 0;
}

.table-body {
  background-color: white;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s ease;
  align-items: center;
  min-height: 80px;
}

.table-row:hover {
  background-color: #f8fafc;
}

.table-row:last-child {
  border-bottom: none;
}

.table-cell {
  padding: 16px;
  display: flex;
  align-items: center;
}

/* Header Cell Styles */
.header-cell {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Column Widths */
.post-cell {
  flex: 0 0 140px;
  justify-content: flex-start;
}

.content-cell {
  flex: 1;
  min-width: 300px;
}

.locations-cell {
  flex: 0 0 280px;
}

.actions-cell {
  flex: 0 0 150px;
  justify-content: center;
}

/* Post Cell Styles */
.post-number {
  background: #8056dd;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.post-image-container-small {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.post-image-container-small:hover {
  transform: scale(1.1);
  border-color: #8056dd;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.post-image-small {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Content Cell Styles */
.content-section {
  width: 100%;
}

.scheduled-time {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.time-text {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.post-text {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Locations Cell Styles */
.locations-dropdown-container {
  width: 100%;
}

.locations-dropdown-btn {
  font-size: 12px !important;
  height: 32px !important;
  min-width: 180px !important;
  text-transform: none !important;
  justify-content: space-between !important;
}

.locations-dropdown-card {
  min-width: 300px;
  max-width: 400px;
  max-height: 400px;
}

/* Search Container */
.search-container {
  padding: 12px 16px 8px 16px;
  border-bottom: 1px solid #f1f5f9;
}

.search-field {
  font-size: 14px;
}

/* Select All Container */
.select-all-container {
  padding: 8px 16px;
  border-bottom: 1px solid #f1f5f9;
  background-color: #f8fafc;
}

.select-all-checkbox {
  margin: 0;
}

.select-all-text {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

/* Dropdown Locations List */
.dropdown-locations-list {
  max-height: 240px;
  overflow-y: auto;
  padding: 4px 0;
}

.dropdown-location-item {
  padding: 4px 16px;
  transition: background-color 0.2s ease;
}

.dropdown-location-item:hover {
  background-color: #f8fafc;
}

.location-dropdown-checkbox {
  margin: 0;
  width: 100%;
}

.location-dropdown-info {
  width: 100%;
  margin-left: 8px;
}

.location-dropdown-name {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
}

.location-dropdown-address {
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.3;
  margin-top: 2px;
}

.no-locations {
  display: flex;
  align-items: center;
  gap: 6px;
}

.no-locations-text {
  font-size: 13px;
  color: #059669;
  font-weight: 500;
}

/* Actions Cell Styles */
.connect-btn {
  font-size: 12px !important;
  height: 32px !important;
  min-width: 120px !important;
}

.action-icons {
  display: flex;
  gap: 8px;
}

/* Page level button styles */
.page-add-all-btn {
  font-size: 14px !important;
  height: 36px !important;
  min-width: 180px !important;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .table-header,
  .table-row {
    font-size: 13px;
  }

  .post-cell {
    flex: 0 0 120px;
  }

  .locations-cell {
    flex: 0 0 240px;
  }

  .actions-cell {
    flex: 0 0 130px;
  }
}

@media (max-width: 768px) {
  .table-header {
    display: none;
  }

  .table-row {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
    min-height: auto;
  }

  .table-cell {
    width: 100%;
    padding: 8px 0;
  }

  .post-cell {
    flex-direction: row;
    justify-content: flex-start;
    border-bottom: 1px solid #f1f5f9;
    padding-bottom: 12px;
    margin-bottom: 12px;
  }

  .content-cell,
  .locations-cell,
  .actions-cell {
    flex: none;
    min-width: 100%;
  }

  .connect-btn {
    width: 100% !important;
    min-width: 100% !important;
  }

  /* Mobile dropdown adjustments */
  .locations-dropdown-btn {
    min-width: 150px !important;
    font-size: 11px !important;
  }

  .locations-dropdown-card {
    min-width: 280px;
    max-width: 320px;
  }

  .dropdown-locations-list {
    max-height: 200px;
  }
}
</style>
